import { createContext, useContext, useEffect, useState } from 'react'
import api from '../services/api'

/**
 * Whose product this is, as far as the person looking at it is concerned.
 *
 * FROM THE ADDRESS, NEVER FROM THE PERSON. A company is identified by the address in the
 * browser, so somebody who administers Acme still sees ProTrack while they are on our own
 * address — because on our address they are doing our work. Branding that followed the person
 * instead would put a customer's name on our own screens, and on the screens they use for
 * another customer.
 *
 * Ours is the default for everything unset, so this changes nothing until a company is actually
 * served at its own address.
 */
const OURS = {
  name: 'ProTrack',
  productName: 'ProTrack',
  logoUrl: '/3pillars-logo.png',
  primaryColour: '',          // empty means "leave the stylesheet alone"
  emailFooter: '',
  organisation: null,
}

const BrandingContext = createContext(OURS)

export function useBranding() {
  return useContext(BrandingContext)
}

/**
 * A company's colour, only when it is one we can safely build on.
 *
 * Returns null unless it is a plain #RRGGBB. Anything else — a colour name, an rgb() string —
 * is left alone rather than guessed at, because the tint below appends an alpha channel to the
 * hex and would produce nonsense for any other notation.
 */
export function brandHex(colour) {
  return /^#[0-9a-f]{6}$/i.test(colour || '') ? colour : null
}

/** The same colour at roughly 8% — the faint wash behind a selected menu item. */
export function brandTint(colour) {
  const hex = brandHex(colour)
  return hex ? `${hex}14` : null
}

/** How bright a colour is to the eye, 0 (black) to 1 (white). The usual sRGB formula. */
function luminance(hex) {
  const chan = (i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * chan(1) + 0.7152 * chan(3) + 0.0722 * chan(5)
}

/**
 * Text that can be read ON the brand colour — white on a dark one, near-black on a pale one.
 *
 * A customer choosing a pastel got white text on it and a button nobody could read. Their colour
 * is their choice; whether the writing on it is legible is ours.
 */
export function brandTextOn(colour) {
  const hex = brandHex(colour)
  if (!hex) return null
  return luminance(hex) > 0.45 ? '#1f2937' : '#ffffff'
}

/**
 * The brand colour as TEXT on a near-white background, darkened until it can be read.
 *
 * The selected menu item is the company's colour on a very faint wash of the same colour, which
 * works for a strong colour and disappears for a pale one. Darkened in place rather than swapped
 * for grey, so it still reads as their colour.
 */
export function brandInk(colour) {
  const hex = brandHex(colour)
  if (!hex) return null
  let [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16))
  // Step it down until it is dark enough to read, rather than by a fixed amount — a mid colour
  // needs one nudge and a near-white one needs several.
  let guard = 0
  while (luminance(`#${[r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')}`) > 0.28 && guard++ < 12) {
    ;[r, g, b] = [r, g, b].map(v => Math.round(v * 0.82))
  }
  return `#${[r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')}`
}

/** Turn what the server sends into what a screen needs, with ours filling every gap. */
export function brandingFrom(payload) {
  const b = payload?.branding || {}
  return {
    name: b.name || OURS.name,
    productName: b.product_name || b.name || OURS.productName,
    // A company that has set no logo gets ours rather than a broken image.
    logoUrl: b.logo_url || OURS.logoUrl,
    primaryColour: b.primary_colour || OURS.primaryColour,
    emailFooter: b.email_footer || OURS.emailFooter,
    organisation: payload?.organisation || null,
  }
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(OURS)

  useEffect(() => {
    let alive = true
    // The PUBLIC endpoint, not /current. This has to answer on the sign-in page too, where
    // nobody has a token yet — and that is the one screen where a customer's name matters most.
    api.get('/organisations/branding')
      .then(({ data }) => { if (alive) setBranding(brandingFrom(data)) })
      // Ours on any failure. A company's name not loading must never stop the application
      // loading, and being signed out is one of the ways this fails.
      .catch(() => { if (alive) setBranding(OURS) })
    return () => { alive = false }
  }, [])

  // The tab, and anything the browser shows outside our markup.
  useEffect(() => {
    document.title = branding.productName
  }, [branding.productName])

  // The favicon too — it was the one thing left saying ProTrack on a customer's tab, and a tab
  // is exactly where somebody looks while switching between windows. The title changed and the
  // icon beside it did not, which reads as a mistake rather than as a neutral default.
  //
  // Only when a company set a logo. With none, this leaves the markup's own icon alone rather
  // than pointing it at ours explicitly — same rule as the logo: unset changes nothing.
  useEffect(() => {
    if (branding.logoUrl === OURS.logoUrl) return
    const link = document.querySelector("link[rel~='icon']")
    if (link) link.href = branding.logoUrl
  }, [branding.logoUrl])

  // One custom property, set only when a company chose a colour. Left unset, every existing
  // stylesheet rule applies exactly as it does today — the switch turns nothing off on arrival.
  useEffect(() => {
    const root = document.documentElement
    if (branding.primaryColour) {
      root.style.setProperty('--brand', branding.primaryColour)
    } else {
      root.style.removeProperty('--brand')
    }
  }, [branding.primaryColour])

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  )
}
